/**
 * Research Workspace Container
 *
 * Main container for the Evidence Graph workspace
 * Includes: Evidence Board + Node Detail Panel + Notes Scratchpad
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import EvidenceBoard from './EvidenceBoard';
import NodeDetailPanel, { NodeDetailData, FindingInfo } from './NodeDetailPanel';
import UnifiedFindingsPanel, { CitationContext, FindingsToggleButton } from './UnifiedFindingsPanel';
// OnlineSupportPanel / OnlineSupportToggleButton are no longer mounted
// here — online support items render as a synthetic group inside the
// UnifiedFindingsPanel (BAR-39 UI consolidation).
import ValidationStatusPill, { computeValidationState } from './ValidationStatusPill';
import ResearchActivityChecklist from './ResearchActivityChecklist';
import ResearchWelcomePanel from '../process/ResearchWelcomePanel';
import { LogicalChainGraph, LogicalNode, isDynamicNodeType, NodeType, ReferenceDetails } from './types';
import { DictWorkspaceState, DictMarker, DictUploadedFile } from '../../types/workspaceState';
import type { Cluster } from '../../types/cluster';
import type { OnlineSupport } from '../../types/online_support';
import { isDictWorkspaceFormat, dictNodesToArray } from '../../utils/workspaceConversions';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Parse the detail field which may be a Python-style dict string
 * Returns the extracted text or the original string
 */
function parseDetailText(detail: string | undefined): string | undefined {
  if (!detail) return undefined;
  // If it looks like a Python dict string, try to extract the text
  if (detail.startsWith('{') && detail.includes("'text':")) {
    try {
      // Convert Python-style single quotes to JSON double quotes
      const jsonStr = detail
        .replace(/'/g, '"')
        .replace(/None/g, 'null')
        .replace(/True/g, 'true')
        .replace(/False/g, 'false');
      const parsed = JSON.parse(jsonStr);
      return parsed.text || detail;
    } catch {
      // If parsing fails, return the raw detail
      return detail;
    }
  }
  return detail;
}

// =============================================================================
// TYPES
// =============================================================================

/** Reference data structure (matches report references) */
interface Reference {
  id: string;
  title?: string;
  summary?: string;
  detail?: string;
  document?: string;
  document_reference?: string;
}

// =============================================================================
// HELPERS - Convert LogicalNode to EvidenceData
// =============================================================================

/** Finding data structure from project - matches DictFinding from workspaceState.ts */
interface Finding {
  finding_id: string;
  source_document_celex?: string;
  source_document_name?: string;
  source_reference_hierarchy?: string;
  source_reference?: string;
  source_text?: string;       // Full text of the article/section (the actual finding!)
  source_snippet?: string;    // Specific relevant excerpt
  extracted_at?: string;
  tool_used?: string;
  document_version?: string;
  // Enriched source metadata
  source_type?: string;
  document_type?: string;
  document_full_title?: string;
  article_title?: string;
  source_service?: string;
  source_url?: string;
}

/**
 * Convert Finding to FindingInfo format for NodeDetailPanel
 */
function findingToFindingInfo(finding: Finding): FindingInfo {
  return {
    finding_id: finding.finding_id,
    content: finding.source_snippet || finding.source_text || '',
    source_document: finding.source_document_name,
    source_reference: finding.source_reference,
  };
}

/**
 * Convert a Record of Findings to a Record of FindingInfo
 */
function findingsToFindingInfoMap(
  findings: Record<string, Finding> | undefined
): Record<string, FindingInfo> | undefined {
  if (!findings) return undefined;
  const result: Record<string, FindingInfo> = {};
  for (const [key, finding] of Object.entries(findings)) {
    result[key] = findingToFindingInfo(finding);
  }
  return result;
}

/**
 * Convert a LogicalNode to NodeDetailData format for the bottom detail panel
 */
function nodeToDetailData(
  node: LogicalNode | null,
  references?: Reference[],
  allNodes?: LogicalNode[],
  edges?: Record<string, string[]>,
): NodeDetailData | null {
  if (!node) return null;

  let detail: string | undefined;
  let source: string | undefined;
  let sourceReference: string | undefined;

  // Dynamic nodes have inline content
  if (isDynamicNodeType(node.node_type)) {
    detail = node.content || parseDetailText(node.detail ?? undefined);
  } else {
    detail = parseDetailText(node.detail ?? undefined);

    // Check for source info - first try source_metadata, then top-level fields
    if (node.source_metadata) {
      source = node.source_metadata.source_document;
      sourceReference = node.source_metadata.source_reference;
    }
    // Fall back to top-level source fields (from API workspace format)
    if (!source && (node as any).source_document) {
      source = (node as any).source_document;
    }
    if (!sourceReference && (node as any).source_reference) {
      sourceReference = (node as any).source_reference;
    }

    // Fall back to references
    if (!detail && node.reference_ids.length > 0 && references) {
      const ref = references.find(r => node.reference_ids.includes(r.id));
      if (ref) {
        detail = parseDetailText(ref.detail) || ref.summary;
        if (!source) source = ref.document;
        if (!sourceReference) sourceReference = ref.document_reference;
      }
    }
  }

  return {
    nodeId: node.node_id,
    nodeType: node.node_type as NodeType,
    label: node.label,
    content: node.content,  // Use actual content, not relevance_note
    detail,
    relevanceNote: node.relevance_note,  // Why this matters - the reasoning/logic
    findingIds: node.finding_ids,
    sourceDocument: source,
    sourceReference,
    // Plan graph (#89)
    workspaceType: (node as any).workspace_type,
    workspaceId: (node as any).workspace_id,
    deliverable: (node as any).deliverable,
    // Derive inputs from incoming edges (upstream node deliverables)
    inputs: (() => {
      if (!edges || !allNodes) return undefined;
      const inputs: Array<{ deliverable: string; fromLabel: string }> = [];
      for (const [sourceId, targetIds] of Object.entries(edges)) {
        if (targetIds.includes(node.node_id)) {
          const sourceNode = allNodes.find(n => n.node_id === sourceId);
          const srcDeliverable = (sourceNode as any)?.deliverable;
          if (srcDeliverable) {
            inputs.push({ deliverable: srcDeliverable, fromLabel: sourceNode?.label || 'Unknown' });
          }
        }
      }
      return inputs.length > 0 ? inputs : undefined;
    })(),
    // Validation (#89)
    validationStatus: (node as any).validation_status || null,
    reviews: (node as any).reviews?.map((r: any) => ({
      reviewer_id: r.reviewer_id || r.reviewer_type || 'unknown',
      channel: r.channel || 'general',
      verdict: r.verdict || 'unknown',
      rationale: r.rationale || '',
      severity: r.severity || 'info',
    })) || undefined,
  };
}

// =============================================================================
// NOTES SCRATCHPAD PANEL
// =============================================================================

interface NotesPanelProps {
  notes: string;
  onNotesChange: (notes: string) => void;
  isOpen: boolean;
  onToggle: () => void;
}

function NotesPanel({ notes, onNotesChange, isOpen, onToggle }: NotesPanelProps) {
  return (
    <>
      {/* Toggle Button - Always visible */}
      <button
        onClick={onToggle}
        className={`absolute top-4 left-4 z-20 p-2 rounded-lg shadow-lg border transition-all ${
          isOpen
            ? 'bg-accent text-white border-border-default'
            : 'bg-surface/90 backdrop-blur-md text-fg-secondary border-border-default/50 hover:bg-surface'
        }`}
        title={isOpen ? 'Close notes' : 'Open notes'}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </button>

      {/* Notes Panel - Slides in from left */}
      <div
        className={`absolute top-0 left-0 h-full bg-surface border-r border-border-default shadow-lg z-10 transition-all duration-300 flex flex-col ${
          isOpen ? 'w-80 opacity-100' : 'w-0 opacity-0 overflow-hidden'
        }`}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-border-default bg-bg-warm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <h3 className="font-semibold text-fg-secondary">Research notes</h3>
            </div>
            <button
              onClick={onToggle}
              className="p-1 hover:bg-bg-warm rounded transition-colors"
            >
              <svg className="w-4 h-4 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Notes Content */}
        <div className="flex-1 p-4 overflow-hidden">
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Write your research notes here...

• Key findings
• Questions to explore
• Important connections
• Hypotheses"
            className="w-full h-full resize-none border border-border-default rounded-lg p-3 text-sm text-fg-secondary placeholder-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          />
        </div>

        {/* Footer with character count */}
        <div className="flex-shrink-0 px-4 py-2 border-t border-divider bg-bg-warm">
          <p className="text-xs text-fg-tertiary">
            {notes.length} characters
          </p>
        </div>
      </div>
    </>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

interface ResearchWorkspaceProps {
  /** Optional external graph data. Can be LogicalChainGraph (arrays) or DictWorkspaceState (dicts). */
  graph?: LogicalChainGraph | DictWorkspaceState;
  /** References from the linked report (for looking up evidence content) */
  references?: Reference[];
  /** Findings from the project (for looking up finding content when clicked) */
  findings?: Record<string, Finding>;
  /** Reference markers from workspace state */
  markers?: Record<string, DictMarker>;
  /** Cluster regions from workspace state */
  clusters?: Record<string, Cluster>;
  /** Enable cluster-region rendering for this workspace surface. */
  enableClusters?: boolean;
  /** Online support records from online_search pins */
  onlineSupport?: Record<string, OnlineSupport>;
  /** User-uploaded workspace source assets */
  uploadedFiles?: Record<string, DictUploadedFile>;
  /** Workspace ID for backend API calls (e.g., fetch-reference) */
  workspaceId?: string;
  /** Compartment ID for backend API calls */
  compartmentId?: string;
  /** Enable editing mode (drag nodes, etc.) */
  editable?: boolean;
  /** Initial node positions (from saved UI state) */
  initialPositions?: Record<string, { x: number; y: number }>;
  /** Callback when node positions change (for auto-save) */
  onPositionsChange?: (positions: Record<string, { x: number; y: number }>) => void;
  /** Sync status indicator component (optional, rendered in header) */
  syncStatusComponent?: React.ReactNode;
  /** Research notes content */
  notes?: string;
  /** Callback when notes change */
  onNotesChange?: (notes: string) => void;
  /** Node ID to highlight (from reference click). Auto-clears after animation. */
  highlightedNodeId?: string | null;
  /** Whether the source panel is open */
  isSourcePanelOpen?: boolean;
  /** Content for the source panel */
  sourcePanelContent?: ReferenceDetails | null;
  /** Callback to close the source panel */
  onCloseSourcePanel?: () => void;
  /** Callback to navigate to a node from source panel */
  onNavigateToNode?: (nodeId: string) => void;
  /** Trigger re-layout when this value changes (e.g., on research completion) */
  triggerReLayout?: number;
  /** Callback when user clicks the validation status pill */
  onValidateClick?: () => void;
  /** Callback when user double-clicks a node (e.g., to open its workspace) */
  onNodeDoubleClick?: (nodeId: string) => void;
  /** Optional node rendered inside the graph column (not over the findings panel).
   *  Used by the parent (App.tsx / BuildArtifactsView) to inject the Report/Graph/Search
   *  tab pill so its absolute `left-1/2` centering is bounded to the graph column. */
  headerSlot?: React.ReactNode;
  /** Project is truly fresh — no report, no graph nodes, no findings.
   *  When true the workspace is replaced by the Welcome-to-Research
   *  walkthrough. Once any artifact lands, the normal graph view takes
   *  over even if the graph itself is still empty. Required so the prop
   *  always reflects the canonical heuristic computed via
   *  utils/welcomeState.ts in the parent. */
  isWelcomeState: boolean;
}

export default function ResearchWorkspace({
  graph,
  references,
  findings,
  markers,
  clusters,
  enableClusters = true,
  onlineSupport,
  uploadedFiles,
  workspaceId,
  compartmentId,
  editable = false,
  initialPositions,
  onPositionsChange,
  syncStatusComponent,
  notes = '',
  onNotesChange,
  highlightedNodeId,
  isSourcePanelOpen = false,
  sourcePanelContent,
  onCloseSourcePanel,
  onNavigateToNode,
  triggerReLayout,
  onValidateClick,
  onNodeDoubleClick,
  headerSlot,
  isWelcomeState,
}: ResearchWorkspaceProps) {
  // Convert graph to array format if dict format, or use empty graph
  // EvidenceBoard handles its own conversion, but we need array format for node lookup
  const workspaceGraph = useMemo(() => {
    if (!graph) {
      return { nodes: [], edges: [] } as LogicalChainGraph;
    }

    // If dict format, convert nodes to array for lookup functions
    if (isDictWorkspaceFormat(graph)) {
      const dictWorkspace = graph as DictWorkspaceState;
      return {
        nodes: dictNodesToArray(dictWorkspace.nodes),
        edges: [], // Edges not needed for node lookup
      } as unknown as LogicalChainGraph;
    }

    return graph as LogicalChainGraph;
  }, [graph]);

  // Extract dict nodes for findings panel mapped/unmapped grouping
  const dictNodes = useMemo(() => {
    if (graph && isDictWorkspaceFormat(graph)) {
      return (graph as DictWorkspaceState).nodes;
    }
    return undefined;
  }, [graph]);

  const workspaceClusters = useMemo(() => {
    if (clusters) {
      return clusters;
    }
    if (graph && isDictWorkspaceFormat(graph)) {
      return (graph as DictWorkspaceState).clusters || {};
    }
    return {};
  }, [clusters, graph]);

  const workspaceOnlineSupport = useMemo(() => {
    if (onlineSupport) return onlineSupport;
    if (graph && isDictWorkspaceFormat(graph)) {
      return (graph as DictWorkspaceState).online_support || {};
    }
    return {};
  }, [graph, onlineSupport]);

  const workspaceUploadedFiles = useMemo(() => {
    if (uploadedFiles) return uploadedFiles;
    if (graph && isDictWorkspaceFormat(graph)) {
      return (graph as DictWorkspaceState).uploaded_files || {};
    }
    return {};
  }, [graph, uploadedFiles]);

  const workspaceLinkedOutputs = useMemo(() => {
    if (graph && isDictWorkspaceFormat(graph)) {
      return (graph as DictWorkspaceState).linked_outputs || [];
    }
    return [];
  }, [graph]);

  // Track selected node (for opening detail panel with description)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  // Track selected finding (for opening detail panel with finding)
  const [selectedFindingId, setSelectedFindingId] = useState<string | null>(null);

  // Track notes panel visibility
  const [isNotesPanelOpen, setIsNotesPanelOpen] = useState(false);

  // Track unified findings panel visibility
  const [isFindingsPanelOpen, setIsFindingsPanelOpen] = useState(false);

  // Track online support sidebar visibility
  // OnlineSupport panel state removed — online support now renders
  // inside UnifiedFindingsPanel as a synthetic document group, and
  // OnlineSupportFocusModal handles detail/explore-deeper view.

  // Citation context from chat reference clicks ("Why Cited" data)
  const [citationContext, setCitationContext] = useState<CitationContext | null>(null);

  // Local notes state if no external handler provided
  const [localNotes, setLocalNotes] = useState(notes);

  // Handle notes change
  const handleNotesChange = useCallback((newNotes: string) => {
    if (onNotesChange) {
      onNotesChange(newNotes);
    } else {
      setLocalNotes(newNotes);
    }
  }, [onNotesChange]);

  // Use external notes if provided, otherwise local
  const currentNotes = onNotesChange ? notes : localNotes;

  // Handle node selection - opens detail panel with node description
  const handleNodeSelect = useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId);
    // Clear finding selection when selecting a node directly
    if (nodeId) {
      setSelectedFindingId(null);
    }
  }, []);

  // Find a node by its ID (searches recursively through children)
  const findNodeById = useCallback((nodeId: string): LogicalNode | null => {
    const searchNode = (node: LogicalNode): LogicalNode | null => {
      if (node.node_id === nodeId) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = searchNode(child);
          if (found) return found;
        }
      }
      return null;
    };
    for (const node of workspaceGraph.nodes) {
      const found = searchNode(node);
      if (found) return found;
    }
    return null;
  }, [workspaceGraph.nodes]);

  // Find the node that contains the selected finding
  const findNodeWithFinding = useCallback((findingId: string): LogicalNode | null => {
    const searchNode = (node: LogicalNode): LogicalNode | null => {
      if (node.finding_ids?.includes(findingId)) return node;
      if (node.children) {
        for (const child of node.children) {
          const found = searchNode(child);
          if (found) return found;
        }
      }
      return null;
    };
    for (const node of workspaceGraph.nodes) {
      const found = searchNode(node);
      if (found) return found;
    }
    return null;
  }, [workspaceGraph.nodes]);

  // Handle finding click - opens unified findings panel with finding content
  // Also highlights the containing node in the graph
  const handleFindingClick = useCallback((findingId: string) => {
    setSelectedFindingId(findingId);
    setCitationContext(null); // No citation context from graph click
    setIsFindingsPanelOpen(true);
    // Find and select the node containing this finding so it's highlighted in the graph
    const containingNode = findNodeWithFinding(findingId);
    if (containingNode) {
      setSelectedNodeId(containingNode.node_id);
    }
  }, [findNodeWithFinding]);

  // Convert SourcePanel data (from chat reference click) into unified panel state
  useEffect(() => {
    if (isSourcePanelOpen && sourcePanelContent) {
      // Set the finding ID to show in detail mode
      setSelectedFindingId(sourcePanelContent.finding_id);
      // Preserve citation context ("Why Cited", etc.)
      setCitationContext({
        context: sourcePanelContent.context,
        citation_text: sourcePanelContent.citation_text,
        label: sourcePanelContent.label,
        node_ids: sourcePanelContent.node_ids,
      });
      // Open the findings panel
      setIsFindingsPanelOpen(true);
      // Signal to parent that we've consumed the source panel data
      onCloseSourcePanel?.();
    }
  }, [isSourcePanelOpen, sourcePanelContent, onCloseSourcePanel]);

  // Get node data for the detail panel - either from node selection or finding selection
  const selectedNode = useMemo(() => {
    if (selectedNodeId) {
      return findNodeById(selectedNodeId);
    }
    if (selectedFindingId) {
      return findNodeWithFinding(selectedFindingId);
    }
    return null;
  }, [selectedNodeId, selectedFindingId, findNodeById, findNodeWithFinding]);

  const findingsCount = findings ? Object.keys(findings).length : 0;
  const onlineSupportCount = Object.keys(workspaceOnlineSupport).length;
  const uploadedFileCount = Object.keys(workspaceUploadedFiles).length;
  const [isGraphDetailOpen, setIsGraphDetailOpen] = useState(false);
  const [graphSplitPercent, setGraphSplitPercent] = useState(50);
  const [graphListWidth, setGraphListWidth] = useState(220);

  // Empty-state welcome panel — parallel to BuildWelcomePanel rendered
  // by PlanGraphView when planNodes.length === 0. Driven by the
  // canonical `isWelcomeState` flag the parent computes via
  // utils/welcomeState.ts, so this view shares the same on/off cliff as
  // ReportView and the Report/Graph tab pill (no second, divergent
  // heuristic here).
  if (isWelcomeState) {
    return (
      <div className="h-full w-full relative bg-bg-warm overflow-hidden">
        {headerSlot}
        <ResearchWelcomePanel />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-bg-warm">
      {/* Sync Status Indicator */}
      {syncStatusComponent && (
        <div className="absolute top-4 left-4 z-20">
          {syncStatusComponent}
        </div>
      )}

      {/* Main Content — horizontal flex: graph area + findings panel */}
      <div className="flex-1 flex overflow-hidden">
        {/* Graph + Node Detail (vertical stack) */}
        <div className={`flex flex-col overflow-hidden min-w-0 ${isGraphDetailOpen ? '' : 'flex-1'}`}
          style={isGraphDetailOpen ? { width: `${graphSplitPercent}%` } : undefined}
        >
          {/* Graph Area — min-h-0 ensures flex shrink works when detail panel opens */}
          <div className="flex-1 min-h-0 relative overflow-hidden">
            {/* Header slot — confined to the graph column so its `left-1/2`
                centering never overlaps the findings panel sibling. */}
            {headerSlot}
            {/* Validation status pill — top-right, avoids legend/zoom controls bottom-left */}
            {(() => {
              const nodeArray = workspaceGraph.nodes || [];
              const { state, issueCount, nodeCount, approvedCount } = computeValidationState(nodeArray);
              return (
                <ValidationStatusPill
                  state={state}
                  issueCount={issueCount}
                  nodeCount={nodeCount}
                  approvedCount={approvedCount}
                  onClick={() => {
                    if (onValidateClick) {
                      onValidateClick();
                    } else {
                      console.log('[ValidationStatusPill] Validate clicked — no handler');
                    }
                  }}
                />
              );
            })()}
            {/* Research activity checklist — top-right inspector showing which
                capabilities this project has used (derived from store events). */}
            <ResearchActivityChecklist />
            <EvidenceBoard
              graph={graph || { nodes: [], edges: [] }}
              clusters={workspaceClusters}
              enableClusters={enableClusters}
              editable={editable}
              initialPositions={initialPositions}
              onPositionsChange={onPositionsChange}
              onNodeSelect={handleNodeSelect}
              onNodeDoubleClick={onNodeDoubleClick}
              onFindingClick={handleFindingClick}
              highlightedNodeId={highlightedNodeId}
              triggerReLayout={triggerReLayout}
              openNodeId={selectedNodeId}
            />
            {/* Toggle button overlays graph's top-right when panel closed */}
            {!isFindingsPanelOpen && (findingsCount > 0 || onlineSupportCount > 0 || uploadedFileCount > 0) && (
              <FindingsToggleButton
                findingsCount={findingsCount + onlineSupportCount + uploadedFileCount}
                onToggle={() => {
                  setIsFindingsPanelOpen(true);
                }}
              />
            )}
          </div>

          {/* Bottom Detail Panel — shows when a node is selected */}
          {selectedNodeId && (
            <NodeDetailPanel
              node={nodeToDetailData(
                selectedNode,
                references,
                workspaceGraph?.nodes,
                graph && 'edges' in graph && !Array.isArray((graph as any).edges) ? (graph as any).edges : undefined,
              )}
              findings={findingsToFindingInfoMap(findings)}
              onClose={() => setSelectedNodeId(null)}
              onFindingClick={handleFindingClick}
              onOpenNode={onNodeDoubleClick}
            />
          )}
        </div>

        {/* Findings panel — flex sibling, compresses graph */}
          <div
            className="h-full flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden"
            style={{
              width: isFindingsPanelOpen
                ? (isGraphDetailOpen ? `${100 - graphSplitPercent}%` : graphListWidth)
                : 0
            }}
          >
            <UnifiedFindingsPanel
              allFindings={findings || {}}
              allMarkers={markers || {}}
              allOnlineSupport={workspaceOnlineSupport}
              allUploadedFiles={workspaceUploadedFiles}
              linkedOutputs={workspaceLinkedOutputs}
              nodes={dictNodes}
              selectedFindingId={selectedFindingId}
              citationContext={citationContext}
              isOpen={isFindingsPanelOpen}
              workspaceId={workspaceId}
              compartmentId={compartmentId}
              onToggle={() => setIsFindingsPanelOpen(!isFindingsPanelOpen)}
              onFindingSelect={(id) => {
                setSelectedFindingId(id);
                setIsFindingsPanelOpen(true);
              }}
              onClose={() => {
                setSelectedFindingId(null);
                setCitationContext(null);
                setIsGraphDetailOpen(false);
                setIsFindingsPanelOpen(false);
              }}
              onNavigateToNode={onNavigateToNode}
              onDetailOpenChange={setIsGraphDetailOpen}
              onPanelResize={(delta) => {
                if (isGraphDetailOpen) {
                  const containerEl = document.querySelector('.flex-1.flex.overflow-hidden') as HTMLElement;
                  const containerWidth = containerEl?.offsetWidth || window.innerWidth;
                  const pctDelta = (delta / containerWidth) * 100;
                  setGraphSplitPercent(p => Math.max(25, Math.min(75, p + pctDelta)));
                } else {
                  setGraphListWidth(w => Math.max(160, Math.min(500, w - delta)));
                }
              }}
            />
          </div>
      </div>
    </div>
  );
}
